import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOrganization } from './OrganizationContext';
import { projectService } from '@/services/projectService';
import type { Project, ProjectCreate, ProjectUpdate } from '@/types/project';

interface ProjectContextType {
  currentProject: Project | null;
  projects: Project[];
  isLoading: boolean;
  error: string | null;
  setCurrentProject: (project: Project | null) => void;
  createProject: (data: Omit<ProjectCreate, 'organizationId'>) => Promise<Project>;
  updateProject: (projectId: string, data: ProjectUpdate) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  refreshProjects: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | null>(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: React.ReactNode;
}

export const ProjectProvider = ({ children }: ProjectProviderProps) => {
  const { currentOrganization } = useOrganization();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  // Start true: projects load on mount, so consumers (e.g. the first-project
  // wizard) must not mistake the pre-load render for "loaded with zero projects".
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The org whose projects are currently in `projects`. Until this matches the
  // selected org, `projects` is stale/empty for it — see effectiveLoading below.
  const [loadedForOrgId, setLoadedForOrgId] = useState<string | null>(null);

  // Load projects when organization changes
  const loadProjects = useCallback(async () => {
    if (!currentOrganization) {
      setProjects([]);
      setCurrentProject(null);
      setLoadedForOrgId(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const loadedProjects = await projectService.getProjects(currentOrganization.id);
      setProjects(loadedProjects);
      setLoadedForOrgId(currentOrganization.id);

      // Try to restore last selected project from localStorage
      const savedProjectId = localStorage.getItem(`gizmodata-current-project-${currentOrganization.id}`);
      if (savedProjectId) {
        const savedProject = loadedProjects.find(p => p.id === savedProjectId);
        if (savedProject) {
          setCurrentProject(savedProject);
        } else if (loadedProjects.length > 0) {
          setCurrentProject(loadedProjects[0]);
        } else {
          setCurrentProject(null);
        }
      } else if (loadedProjects.length > 0) {
        setCurrentProject(loadedProjects[0]);
      } else {
        setCurrentProject(null);
      }
    } catch (err) {
      // A failed load is "unknown", not "this org has no projects": keep whatever we had and
      // mark the org as NOT loaded so onboarding (first-project wizard) never fires on a
      // transient error (Philip 2026-09-25: prompted to create a project that existed).
      setError(err instanceof Error ? err.message : 'Failed to load projects');
      setLoadedForOrgId(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Save current project to localStorage when it changes. Right after an org switch this effect runs once
  // with the NEW org and the OLD org's project still selected (the new org's projects haven't loaded yet);
  // saving then overwrote the new org's remembered project with a foreign id, so switching back always
  // landed on the first project. Only save a project that belongs to the current org.
  useEffect(() => {
    if (currentOrganization && currentProject && currentProject.organizationId === currentOrganization.id) {
      localStorage.setItem(`gizmodata-current-project-${currentOrganization.id}`, currentProject.id);
    }
  }, [currentOrganization, currentProject]);

  const handleSetCurrentProject = (project: Project | null) => {
    setCurrentProject(project);
  };

  const createProject = async (data: Omit<ProjectCreate, 'organizationId'>): Promise<Project> => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    const newProject = await projectService.createProject({
      ...data,
      organizationId: currentOrganization.id,
    });

    setProjects(prev => [...prev, newProject]);

    // If this is the first project, set it as current
    if (projects.length === 0) {
      setCurrentProject(newProject);
    }

    return newProject;
  };

  const updateProject = async (projectId: string, data: ProjectUpdate): Promise<Project> => {
    const updatedProject = await projectService.updateProject(projectId, data);

    setProjects(prev => prev.map(p => p.id === projectId ? updatedProject : p));

    if (currentProject?.id === projectId) {
      setCurrentProject(updatedProject);
    }

    return updatedProject;
  };

  const deleteProject = async (projectId: string): Promise<void> => {
    await projectService.deleteProject(projectId);

    setProjects(prev => prev.filter(p => p.id !== projectId));

    if (currentProject?.id === projectId) {
      const remaining = projects.filter(p => p.id !== projectId);
      setCurrentProject(remaining.length > 0 ? remaining[0] : null);
    }
  };

  const refreshProjects = async (): Promise<void> => {
    await loadProjects();
  };

  // Report "loading" until projects have actually been fetched FOR THE SELECTED
  // org. Closes the null→org transition gap where the no-org branch already set
  // isLoading=false with an empty list, before the org-scoped fetch begins — the
  // window in which the first-project wizard used to fire despite a seeded project.
  const effectiveLoading =
    isLoading || (!!currentOrganization && loadedForOrgId !== currentOrganization.id);

  return (
    <ProjectContext.Provider
      value={{
        currentProject,
        projects,
        isLoading: effectiveLoading,
        error,
        setCurrentProject: handleSetCurrentProject,
        createProject,
        updateProject,
        deleteProject,
        refreshProjects,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
